'use client';

import type { Product } from '@/components/Product/ProductCard';
import { useReporting } from '@/hooks/useReporting';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { priceInfo as getPriceInfo } from '@/utils/price';
import { useCallback, useMemo } from 'react';
import {
  buildCampaignCollectButtonClickProperties,
  buildCampaignProductTrackingProperties,
  getCampaignTrackingCommonProperties,
} from '../[slug]/hooks/campaignTrackingProperties';
import { useCampaignPageTracking } from '../[slug]/hooks/useCampaignPageTracking';
import type { MarketingRuntimeConfig } from '../[slug]/types';

export function useBestsellerTracking(runtimeConfig: MarketingRuntimeConfig) {
  const campaignTracking = useCampaignPageTracking(runtimeConfig);
  const { trackEvent } = useReporting();
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const commonProperties = useMemo(
    () => getCampaignTrackingCommonProperties(runtimeConfig),
    [runtimeConfig],
  );

  const trackCollectButtonClick = useCallback(
    (item: Product, index: number, moduleName: string, collectType: string, tabName?: string) => {
      try {
        const currentPrice = getPriceInfo(userInfo, item, currency);
        const basePrice = getPriceInfo(userInfo, item, env.NEXT_PUBLIC_DEFAULT_CURRENCY);

        trackEvent(
          {
            eventName: 'CampaignPageCollectBtn_Click',
            properties: buildCampaignCollectButtonClickProperties(commonProperties, {
              moduleName,
              tabName,
              collectType,
              pitsIndex: index,
              productProperties: buildCampaignProductTrackingProperties(item, {
                productPrice: parseFloat(currentPrice.price || '0'),
                currentProductPrice: parseFloat(currentPrice.sell_price || '0'),
                baseCurrentProductPrice: parseFloat(basePrice.sell_price || '0'),
                dollarExchangeRate: parseFloat(currency?.exchange_rate || '0'),
                currencyName: currency?.name || '',
              }),
            }),
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Bestsellers collect button tracking error:', error);
      }
    },
    [commonProperties, currency, trackEvent, userInfo],
  );

  return {
    ...campaignTracking,
    trackCollectButtonClick,
  };
}
